import { Button, DatePicker, Form, Input, Space } from "antd";
import { useEffect } from "react";
import { mailRegex } from "../../constants/constants"; // Or use your own regex
import { useInterviewBulkOfferStatusMutation } from "../../features/employee/employeeApi";
import useError from "../../hooks/useError";
import { toast } from "../../lib/toast";
import CustomModal from "../ui/CustomModal";

type Props = {
  open: boolean;
  onClose: () => void;
  id?: string | number;
  status?: string;
};

export default function SendOfferLetterModal({
  open,
  onClose,
  status,
  id,
}: Props) {
  const [form] = Form.useForm();
  const [interviewBulkOfferStatus, { isLoading, isError, error, isSuccess }] =
    useInterviewBulkOfferStatusMutation();
  useError(isError, error, "Offer letter sending failed");

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Offer letter sent successfully");
      onClose && onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, open]);

  const handleFinish = (values: any) => {
    const { salary, ...rest } = values;

    const newValues = {
      interview_ids: [id],
      offer_status: status,
      salary: String(salary),
      ...rest,
    };

    interviewBulkOfferStatus(newValues);
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  return (
    <CustomModal
      title="Send Offer Letter"
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark="optional"
      >
        <div className="grid md:grid-cols-2 gap-x-4">
          <Form.Item
            name="job_start_date"
            label="Job Start Date"
            className="mb-4"
            rules={[
              { required: true, message: "Please select job start date" },
            ]}
          >
            <DatePicker
              size="large"
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Form.Item
            name="reporting_manager"
            label="Reporting Manager Name"
            className="mb-4"
            rules={[
              { required: true, message: "Please enter reporting manager" },
            ]}
          >
            <Input size="large" placeholder="Enter reporting manager" />
          </Form.Item>

          <Form.Item
            name="reporting_manager_designation"
            label="Reporting Manager Designation"
            className="mb-4"
            rules={[{ required: true, message: "Please enter designation" }]}
          >
            <Input size="large" placeholder="Enter manager designation" />
          </Form.Item>

          <Form.Item
            name="salary"
            label="Salary"
            className="mb-4"
            validateFirst
            rules={[
              { required: true, message: "Please enter salary" },
              {
                validator: (_, value) => {
                  if (!value || isNaN(value)) {
                    return Promise.reject(new Error("Salary must be a number"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input size="large" placeholder="Enter salary" />
          </Form.Item>

          <Form.Item
            name="work_location"
            label="Work Location"
            className="mb-4"
            rules={[{ required: true, message: "Please enter work location" }]}
          >
            <Input size="large" placeholder="Enter work location" />
          </Form.Item>

          <Form.Item
            name="response_deadline"
            label="Response Deadline"
            className="mb-4"
            rules={[
              { required: true, message: "Please select response deadline" },
            ]}
          >
            <DatePicker
              size="large"
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Form.Item
            name="response_email"
            label="Response Email"
            className="mb-4"
            rules={[
              { required: true, message: "Please enter response email" },
              {
                validator: (_, value) => {
                  if (!value || mailRegex.test(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Enter a valid email"));
                },
              },
            ]}
          >
            <Input size="large" placeholder="Enter response email" />
          </Form.Item>

          <Form.Item
            name="hr_name"
            label="HR Name"
            className="mb-4"
            rules={[{ required: true, message: "Please enter HR name" }]}
          >
            <Input size="large" placeholder="Enter HR name" />
          </Form.Item>

          <Form.Item
            name="hr_designation"
            label="HR Designation"
            className="mb-4"
            rules={[{ required: true, message: "Please enter HR designation" }]}
          >
            <Input size="large" placeholder="Enter HR designation" />
          </Form.Item>
        </div>

        <Space className="w-full mt-4 flex justify-end">
          <Button className="modal-button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={isLoading}
            type="primary"
            htmlType="submit"
            className="modal-button"
          >
            Submit
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
